import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AnalyzeButton } from './AnalyzeButton';
import { Button } from './ui/button';
import { SectionSizeCell } from './SectionSizeCell';
import { InlineEditableText } from './InlineEditableText';
import { computeAverage } from '../utils/section-size-comparison';
import { handleModifierAwareNavigate } from '../utils/app-navigation';
import { ButtonTooltip, TooltipProvider } from './ui/tooltip';

export type EditableChapterAnalysisField =
  | 'summary'
  | 'mood'
  | 'tensionLevel'
  | 'actionLevel'
  | 'plotQuestionsOpened'
  | 'plotQuestionsClosed';

interface ChapterAnalysisData {
  summary: string;
  mood: string;
  tensionLevel: number;
  actionLevel: number;
  plotQuestionsOpened: string[];
  plotQuestionsClosed: string[];
  manualChapterMatch?: {
    matchedManualChapterId: string | null;
    matchedManualChapterOrder: number | null;
    contentMatchScore: number;
    contentMatchExplanation: string;
    wordCountMatch: {
      actual: number;
      planned: number | null;
      difference: number | null;
      matchScore: number;
    };
    orderMatch: {
      actualPosition: number | null;
      plannedPosition: number | null;
      difference: number | null;
      matchScore: number;
    };
  };
}

interface Chapter {
  id?: string;
  title: string;
  wordCount: number;
  readingTime: number;
  estimatedPages: number;
  analysis: ChapterAnalysisData | null;
  number?: number;
  order?: number;
  wordsGoal?: number;
  summary?: string;
  isManual?: boolean;
}

interface ChaptersByLevel {
  [level: number]: Chapter[];
}

interface DocumentChaptersProps {
  chaptersByLevel: ChaptersByLevel;
  isSidebar: boolean;
  analyzingChapters: Set<string>;
  expandedAnalyses: Set<string>;
  onAnalyzeAll: () => void;
  onAnalyzeChapter: (chapterTitle: string) => void;
  onToggleAnalysisExpanded: (chapterTitle: string) => void;
  onAddToOutline?: (chapter: { title: string; summary?: string }) => void;
  addingToOutlineChapters: Set<string>;
  getDisplayTitle: (title: string) => string;
  analyzingDocument: boolean;
  showAnalyzeAllButton?: boolean;
  getSectionHref?: (title: string, level: number) => string;
  onScrollToSection?: (title: string, level: number) => void;
  canEditAnalysis?: boolean;
  savingAnalysisField?: boolean;
  onPersistChapterAnalysisField?: (
    chapterTitle: string,
    field: EditableChapterAnalysisField,
    value: string | number | string[],
  ) => Promise<boolean>;
  t: (key: string) => string;
}

export const DocumentChapters: React.FC<DocumentChaptersProps> = ({
  chaptersByLevel,
  isSidebar,
  analyzingChapters,
  expandedAnalyses,
  onAnalyzeAll,
  onAnalyzeChapter,
  onToggleAnalysisExpanded,
  onAddToOutline,
  addingToOutlineChapters,
  getDisplayTitle,
  analyzingDocument,
  showAnalyzeAllButton = true,
  getSectionHref,
  onScrollToSection,
  canEditAnalysis = false,
  savingAnalysisField = false,
  onPersistChapterAnalysisField,
  t,
}) => {
  const navigate = useNavigate();

  const handleSectionLinkActivate = (
    event: React.MouseEvent<HTMLAnchorElement>,
    title: string,
    level: number,
    href: string,
  ): void => {
    event.stopPropagation();
    if (onScrollToSection && !event.metaKey && !event.ctrlKey && event.button === 0) {
      event.preventDefault();
      onScrollToSection(title, level);
      return;
    }
    handleModifierAwareNavigate(event, navigate, href);
  };
  const allChapters: Chapter[] = [];
  Object.values(chaptersByLevel).forEach((chapters) => {
    allChapters.push(...chapters);
  });

  const extractedChapters = allChapters.filter((ch) => !ch.isManual);

  if (extractedChapters.length === 0) {
    return null;
  }

  return (
    <>
      {showAnalyzeAllButton && extractedChapters.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
          <AnalyzeButton
            onClick={onAnalyzeAll}
            isAnalyzing={analyzingDocument}
            variant="ghost"
          >
            {analyzingDocument
              ? t('analyzing_document')
              : t('analyze_full_document')}
          </AnalyzeButton>
        </div>
      )}

      {extractedChapters.length > 0 ? (
        <React.Fragment>
          {Object.keys(chaptersByLevel)
            .map(Number)
            .sort((a, b) => a - b)
            .filter((level) => {
              const chapters = chaptersByLevel[level].filter((ch) => !ch.isManual);
              return chapters.length > 0;
            })
            .map((level) => {
              const chapters = chaptersByLevel[level].filter((ch) => !ch.isManual);
              const averageWordCount = computeAverage(chapters.map((chapter) => chapter.wordCount));
              const averageEstimatedPages = computeAverage(chapters.map((chapter) => chapter.estimatedPages));
              return (
                <div
                  key={level}
                  style={{
                    marginBottom: level === 1 ? '2rem' : '1.5rem',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '1rem',
                    }}
                  >
                    <h2
                      style={{
                        fontSize: '1.25rem',
                        fontWeight: '600',
                        margin: 0,
                        color: 'var(--foreground)',
                      }}
                    >
                      {t('level_with_number').replace('{level}', level.toString())}
                    </h2>
                  </div>
                  <div
                    style={{
                      backgroundColor: 'var(--card)',
                      border: '1px solid var(--border)',
                      borderRadius: '8px',
                      overflow: 'hidden',
                    }}
                  >
                    <div style={{ overflow: 'auto', maxWidth: '100%' }}>
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          tableLayout: 'auto',
                        }}
                      >
                        <thead>
                          <tr style={{ backgroundColor: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
                            <th
                              style={{
                                padding: '1rem',
                                textAlign: 'left',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                color: 'var(--foreground)',
                                borderBottom: '1px solid var(--border)',
                              }}
                            >
                              {t('title')}
                            </th>
                            <th
                              style={{
                                padding: '1rem',
                                textAlign: 'right',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                color: 'var(--foreground)',
                                borderBottom: '1px solid var(--border)',
                              }}
                            >
                              {t('words')}
                            </th>
                            <th
                              style={{
                                padding: '1rem',
                                textAlign: 'right',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                color: 'var(--foreground)',
                                borderBottom: '1px solid var(--border)',
                              }}
                            >
                              {t('estimated_pages')}
                            </th>
                            <th
                              style={{
                                padding: '1rem',
                                textAlign: 'center',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                color: 'var(--foreground)',
                                borderBottom: '1px solid var(--border)',
                              }}
                            >
                              {t('tension')}
                            </th>
                            <th
                              style={{
                                padding: '1rem',
                                textAlign: 'center',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                color: 'var(--foreground)',
                                borderBottom: '1px solid var(--border)',
                              }}
                            >
                              {t('action')}
                            </th>
                            <th
                              style={{
                                padding: '1rem',
                                textAlign: 'center',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                color: 'var(--foreground)',
                                borderBottom: '1px solid var(--border)',
                              }}
                            >
                              {t('questions_open_closed')}
                            </th>
                            <th
                              style={{
                                padding: '1rem',
                                textAlign: 'center',
                                fontWeight: '600',
                                fontSize: '0.875rem',
                                color: 'var(--foreground)',
                                borderBottom: '1px solid var(--border)',
                              }}
                            >
                              {t('analysis')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {chapters.map((chapter, index) => (
                            <React.Fragment key={index}>
                              <tr
                                style={{
                                  borderBottom: index < chapters.length - 1 ? '1px solid var(--border)' : 'none',
                                  transition: 'background-color 0.2s',
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  onToggleAnalysisExpanded(chapter.title);
                                }}
                                onMouseEnter={(e) => {
                                  (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'var(--muted)';
                                }}
                                onMouseLeave={(e) => {
                                  (e.currentTarget as HTMLTableRowElement).style.backgroundColor = 'transparent';
                                }}
                              >
                                <td style={{ padding: '1rem', fontSize: '0.875rem', color: 'var(--foreground)' }}>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    {chapter.isManual && chapter.order && (
                                      <span style={{ color: '#6b7280', fontWeight: '500' }}>
                                        {chapter.order}.
                                      </span>
                                    )}
                                    {getSectionHref && !chapter.isManual ? (
                                      <TooltipProvider delayDuration={0}>
                                        <ButtonTooltip tooltip={t('go_to_document_section')} side="top">
                                          <a
                                            href={getSectionHref(chapter.title, level)}
                                            onClick={(event) => {
                                              handleSectionLinkActivate(
                                                event,
                                                chapter.title,
                                                level,
                                                getSectionHref(chapter.title, level),
                                              );
                                            }}
                                            onAuxClick={(event) => {
                                              handleSectionLinkActivate(
                                                event,
                                                chapter.title,
                                                level,
                                                getSectionHref(chapter.title, level),
                                              );
                                            }}
                                            style={{
                                              color: 'var(--primary)',
                                              textAlign: 'left',
                                              cursor: 'pointer',
                                              textDecoration: 'underline',
                                              textDecorationColor: 'transparent',
                                            }}
                                            onMouseEnter={(event) => {
                                              event.currentTarget.style.textDecorationColor = 'currentColor';
                                            }}
                                            onMouseLeave={(event) => {
                                              event.currentTarget.style.textDecorationColor = 'transparent';
                                            }}
                                          >
                                            {getDisplayTitle(chapter.title)}
                                          </a>
                                        </ButtonTooltip>
                                      </TooltipProvider>
                                    ) : (
                                      getDisplayTitle(chapter.title)
                                    )}
                                    {chapter.isManual && (
                                      <span
                                        style={{
                                          fontSize: '0.625rem',
                                          fontWeight: '600',
                                          backgroundColor: '#10b981',
                                          color: 'white',
                                          padding: '0.125rem 0.375rem',
                                          borderRadius: '9999px',
                                          textTransform: 'uppercase',
                                        }}
                                      >
                                        Manual
                                      </span>
                                    )}
                                  </span>
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'right', fontSize: '0.875rem', color: '#6b7280' }}>
                                  {chapter.isManual && chapter.wordsGoal ? (
                                    `${chapter.wordCount.toLocaleString()} / ${chapter.wordsGoal.toLocaleString()}`
                                  ) : (
                                    <SectionSizeCell
                                      value={chapter.wordCount}
                                      average={averageWordCount}
                                      formattedValue={chapter.wordCount.toLocaleString()}
                                      unitLabel={t('words').toLowerCase()}
                                      t={t}
                                    />
                                  )}
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'right', fontSize: '0.875rem', color: '#6b7280' }}>
                                  <SectionSizeCell
                                    value={chapter.estimatedPages}
                                    average={averageEstimatedPages}
                                    formattedValue={String(chapter.estimatedPages)}
                                    unitLabel={t('estimated_pages').toLowerCase()}
                                    t={t}
                                  />
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem' }}>
                                  {chapter.analysis ? (
                                    <div
                                      title={`${chapter.analysis.tensionLevel}/10`}
                                      style={{
                                        width: '60px',
                                        height: '8px',
                                        backgroundColor: 'var(--muted)',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        margin: '0 auto',
                                        cursor: 'help',
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: `${(chapter.analysis.tensionLevel / 10) * 100}%`,
                                          height: '100%',
                                          backgroundColor: chapter.analysis.tensionLevel >= 7 ? '#ef4444' : chapter.analysis.tensionLevel >= 4 ? '#f59e0b' : '#10b981',
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <span style={{ color: '#9ca3af' }}>-</span>
                                  )}
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem' }}>
                                  {chapter.analysis ? (
                                    <div
                                      title={`${chapter.analysis.actionLevel}/10`}
                                      style={{
                                        width: '60px',
                                        height: '8px',
                                        backgroundColor: 'var(--muted)',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        margin: '0 auto',
                                        cursor: 'help',
                                      }}
                                    >
                                      <div
                                        style={{
                                          width: `${(chapter.analysis.actionLevel / 10) * 100}%`,
                                          height: '100%',
                                          backgroundColor: chapter.analysis.actionLevel >= 7 ? 'var(--scribane-black)' : chapter.analysis.actionLevel >= 4 ? '#8b5cf6' : '#6b7280',
                                        }}
                                      />
                                    </div>
                                  ) : (
                                    <span style={{ color: '#9ca3af' }}>-</span>
                                  )}
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
                                  {chapter.analysis
                                    ? `${chapter.analysis.plotQuestionsOpened.length}/${chapter.analysis.plotQuestionsClosed.length}`
                                    : '-'}
                                </td>
                                <td style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', flexWrap: 'wrap' }} onClick={(e) => e.stopPropagation()}>
                                    {!chapter.isManual && (
                                      <AnalyzeButton
                                        onClick={(e) => {
                                          e?.stopPropagation();
                                          onAnalyzeChapter(chapter.title);
                                        }}
                                        isAnalyzing={analyzingChapters.has(chapter.title)}
                                        variant="ghost"
                                        tooltip={t('analyze_section_tooltip')}
                                      />
                                    )}
                                    {!chapter.isManual && onAddToOutline && (
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e?.stopPropagation();
                                          onAddToOutline({
                                            title: chapter.title,
                                            summary: chapter.analysis?.summary,
                                          });
                                        }}
                                        disabled={addingToOutlineChapters.has(chapter.title)}
                                      >
                                        {addingToOutlineChapters.has(chapter.title)
                                          ? t('adding')
                                          : t('add_to_chapter_outline')}
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              {expandedAnalyses.has(chapter.title) && (
                                <tr>
                                  <td colSpan={7} style={{ padding: '0', backgroundColor: 'var(--muted)' }}>
                                    <div
                                      style={{
                                        padding: '1rem',
                                        borderTop: '1px solid var(--border)',
                                      }}
                                    >
                                      {chapter.analysis ? (
                                      <div
                                        style={{
                                          padding: '1rem',
                                          backgroundColor: 'var(--card)',
                                          borderRadius: '6px',
                                          border: '1px solid var(--border)',
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        {(() => {
                                          const analysis = chapter.analysis;
                                          const editTooltip = t('inline_editable_click_to_edit');
                                          const canInlineEdit = Boolean(canEditAnalysis && onPersistChapterAnalysisField);
                                          const persistField = (
                                            field: EditableChapterAnalysisField,
                                            value: string | number | string[],
                                          ): Promise<boolean> => {
                                            if (!onPersistChapterAnalysisField) {
                                              return Promise.resolve(false);
                                            }
                                            return onPersistChapterAnalysisField(chapter.title, field, value);
                                          };

                                          const commitLevel = (
                                            field: 'tensionLevel' | 'actionLevel',
                                            next: string,
                                          ): Promise<boolean> => {
                                            const parsed = Number.parseInt(next.trim(), 10);
                                            if (!Number.isFinite(parsed) || parsed < 0 || parsed > 10) {
                                              return Promise.resolve(false);
                                            }
                                            return persistField(field, parsed);
                                          };

                                          const renderQuestionList = (
                                            field: 'plotQuestionsOpened' | 'plotQuestionsClosed',
                                            label: string,
                                            questions: string[],
                                          ): React.ReactNode => {
                                            if (!canInlineEdit && questions.length === 0) {
                                              return null;
                                            }

                                            const rows = canInlineEdit ? [...questions, ''] : questions;

                                            return (
                                              <div style={{ marginBottom: '1rem' }}>
                                                <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--foreground)', marginBottom: '0.5rem' }}>
                                                  {label}
                                                </div>
                                                <ul style={{ margin: 0, paddingLeft: canInlineEdit ? 0 : '1.25rem', listStyle: canInlineEdit ? 'none' : undefined }}>
                                                  {rows.map((question, qIndex) => {
                                                    const isAddRow = canInlineEdit && qIndex === questions.length;
                                                    return (
                                                      <li key={isAddRow ? `${field}-add` : `${field}-${qIndex}`} style={{ marginBottom: '0.25rem' }}>
                                                        <InlineEditableText
                                                          value={question}
                                                          onCommit={(next) => {
                                                            if (isAddRow) {
                                                              const trimmed = next.trim();
                                                              if (!trimmed) {
                                                                return false;
                                                              }
                                                              return persistField(field, [...questions, trimmed]);
                                                            }
                                                            const trimmed = next.trim();
                                                            if (!trimmed) {
                                                              return persistField(
                                                                field,
                                                                questions.filter((_, index) => index !== qIndex),
                                                              );
                                                            }
                                                            const nextQuestions = [...questions];
                                                            nextQuestions[qIndex] = trimmed;
                                                            return persistField(field, nextQuestions);
                                                          }}
                                                          savedFieldLabel={label}
                                                          readOnly={!canInlineEdit}
                                                          disabled={savingAnalysisField}
                                                          allowEmpty={!isAddRow}
                                                          multiline
                                                          rows={2}
                                                          placeholder={isAddRow ? t('plot_question_placeholder') : label}
                                                          tooltip={editTooltip}
                                                          stopClickPropagation
                                                          className="w-full font-normal text-sm text-foreground leading-relaxed"
                                                          displayClassName="w-full"
                                                        />
                                                      </li>
                                                    );
                                                  })}
                                                </ul>
                                              </div>
                                            );
                                          };

                                          return (
                                            <>
                                        <div style={{ marginBottom: '1rem' }}>
                                          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--foreground)', marginBottom: '0.5rem' }}>
                                            {t('summary')}
                                          </div>
                                          <InlineEditableText
                                            value={analysis.summary}
                                            onCommit={(next) => persistField('summary', next)}
                                            savedFieldLabel={t('summary')}
                                            readOnly={!canInlineEdit}
                                            disabled={savingAnalysisField}
                                            allowEmpty
                                            multiline
                                            rows={4}
                                            placeholder={t('summary')}
                                            tooltip={editTooltip}
                                            stopClickPropagation
                                            className="w-full font-normal text-sm text-foreground leading-relaxed"
                                            displayClassName="w-full"
                                          />
                                        </div>
                                        <div style={{ marginBottom: '1rem' }}>
                                          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--foreground)', marginBottom: '0.5rem' }}>
                                            {t('mood')}
                                          </div>
                                          <InlineEditableText
                                            value={analysis.mood}
                                            onCommit={(next) => persistField('mood', next)}
                                            savedFieldLabel={t('mood')}
                                            readOnly={!canInlineEdit}
                                            disabled={savingAnalysisField}
                                            allowEmpty
                                            multiline
                                            rows={2}
                                            placeholder={t('mood')}
                                            tooltip={editTooltip}
                                            stopClickPropagation
                                            className="w-full font-normal text-sm text-foreground leading-relaxed"
                                            displayClassName="w-full"
                                          />
                                        </div>
                                        <div style={{ marginBottom: '1rem' }}>
                                          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--foreground)', marginBottom: '0.5rem' }}>
                                            {t('tension_level')}
                                          </div>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <div
                                              style={{
                                                flex: 1,
                                                height: '8px',
                                                backgroundColor: 'var(--muted)',
                                                borderRadius: '4px',
                                                overflow: 'hidden',
                                              }}
                                            >
                                              <div
                                                style={{
                                                  width: `${(analysis.tensionLevel / 10) * 100}%`,
                                                  height: '100%',
                                                  backgroundColor: analysis.tensionLevel >= 7 ? '#ef4444' : analysis.tensionLevel >= 4 ? '#f59e0b' : '#10b981',
                                                }}
                                              />
                                            </div>
                                            <InlineEditableText
                                              value={String(analysis.tensionLevel)}
                                              onCommit={(next) => commitLevel('tensionLevel', next)}
                                              savedFieldLabel={t('tension_level')}
                                              readOnly={!canInlineEdit}
                                              disabled={savingAnalysisField}
                                              placeholder="0"
                                              tooltip={editTooltip}
                                              stopClickPropagation
                                              className="font-normal text-sm text-foreground tabular-nums"
                                              displayClassName="min-w-[1.5rem] justify-end"
                                            />
                                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>/10</span>
                                          </div>
                                        </div>
                                        <div style={{ marginBottom: '1rem' }}>
                                          <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--foreground)', marginBottom: '0.5rem' }}>
                                            {t('action_level')}
                                          </div>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <div
                                              style={{
                                                flex: 1,
                                                height: '8px',
                                                backgroundColor: 'var(--muted)',
                                                borderRadius: '4px',
                                                overflow: 'hidden',
                                              }}
                                            >
                                              <div
                                                style={{
                                                  width: `${(analysis.actionLevel / 10) * 100}%`,
                                                  height: '100%',
                                                  backgroundColor: analysis.actionLevel >= 7 ? 'var(--scribane-black)' : analysis.actionLevel >= 4 ? '#8b5cf6' : '#6b7280',
                                                }}
                                              />
                                            </div>
                                            <InlineEditableText
                                              value={String(analysis.actionLevel)}
                                              onCommit={(next) => commitLevel('actionLevel', next)}
                                              savedFieldLabel={t('action_level')}
                                              readOnly={!canInlineEdit}
                                              disabled={savingAnalysisField}
                                              placeholder="0"
                                              tooltip={editTooltip}
                                              stopClickPropagation
                                              className="font-normal text-sm text-foreground tabular-nums"
                                              displayClassName="min-w-[1.5rem] justify-end"
                                            />
                                            <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>/10</span>
                                          </div>
                                        </div>
                                        {renderQuestionList('plotQuestionsOpened', t('open_questions'), analysis.plotQuestionsOpened)}
                                        {renderQuestionList('plotQuestionsClosed', t('closed_questions'), analysis.plotQuestionsClosed)}
                                        {analysis.manualChapterMatch && (
                                          <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid var(--border)' }}>
                                            <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--foreground)', marginBottom: '1rem' }}>
                                              {t('manual_chapter_match')}
                                            </div>
                                            {analysis.manualChapterMatch.matchedManualChapterId ? (
                                              <>
                                                <div style={{ marginBottom: '0.75rem' }}>
                                                  <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                                                    {t('matched_chapter')}
                                                  </div>
                                                  <div style={{ fontSize: '0.875rem', color: 'var(--foreground)' }}>
                                                    {t('order')}: {analysis.manualChapterMatch.matchedManualChapterOrder}
                                                  </div>
                                                </div>
                                                <div style={{ marginBottom: '0.75rem' }}>
                                                  <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                                                    {t('content_match')}
                                                  </div>
                                                  <div style={{ fontSize: '0.875rem', color: 'var(--foreground)', marginBottom: '0.25rem' }}>
                                                    {analysis.manualChapterMatch.contentMatchScore}/100
                                                  </div>
                                                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                                    {analysis.manualChapterMatch.contentMatchExplanation}
                                                  </div>
                                                </div>
                                                <div style={{ marginBottom: '0.75rem' }}>
                                                  <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                                                    {t('word_count_match')}
                                                  </div>
                                                  <div style={{ fontSize: '0.875rem', color: 'var(--foreground)' }}>
                                                    {t('actual')}: {analysis.manualChapterMatch.wordCountMatch.actual.toLocaleString()}
                                                    {analysis.manualChapterMatch.wordCountMatch.planned !== null && (
                                                      <>
                                                        {' | '}
                                                        {t('planned')}: {analysis.manualChapterMatch.wordCountMatch.planned.toLocaleString()}
                                                        {analysis.manualChapterMatch.wordCountMatch.difference !== null && (
                                                          <>
                                                            {' | '}
                                                            {t('difference')}: {analysis.manualChapterMatch.wordCountMatch.difference > 0 ? '+' : ''}
                                                            {analysis.manualChapterMatch.wordCountMatch.difference.toLocaleString()}
                                                          </>
                                                        )}
                                                      </>
                                                    )}
                                                  </div>
                                                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                                    {t('match_score')}: {analysis.manualChapterMatch.wordCountMatch.matchScore}/100
                                                  </div>
                                                </div>
                                                <div style={{ marginBottom: '0.75rem' }}>
                                                  <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.25rem' }}>
                                                    {t('order_match')}
                                                  </div>
                                                  <div style={{ fontSize: '0.875rem', color: 'var(--foreground)' }}>
                                                    {analysis.manualChapterMatch.orderMatch.actualPosition !== null && (
                                                      <>
                                                        {t('actual_position')}: {analysis.manualChapterMatch.orderMatch.actualPosition}
                                                        {analysis.manualChapterMatch.orderMatch.plannedPosition !== null && (
                                                          <>
                                                            {' | '}
                                                            {t('planned_position')}: {analysis.manualChapterMatch.orderMatch.plannedPosition}
                                                            {analysis.manualChapterMatch.orderMatch.difference !== null && (
                                                              <>
                                                                {' | '}
                                                                {t('difference')}: {analysis.manualChapterMatch.orderMatch.difference > 0 ? '+' : ''}
                                                                {analysis.manualChapterMatch.orderMatch.difference}
                                                              </>
                                                            )}
                                                          </>
                                                        )}
                                                      </>
                                                    )}
                                                  </div>
                                                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                                    {t('match_score')}: {analysis.manualChapterMatch.orderMatch.matchScore}/100
                                                  </div>
                                                </div>
                                              </>
                                            ) : (
                                              <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                                                {t('no_manual_chapter_match')}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        {!chapter.isManual && onAddToOutline && (
                                          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid var(--border)' }}>
                                            <Button
                                              type="button"
                                              variant="secondary"
                                              size="sm"
                                              onClick={(e) => {
                                                e?.stopPropagation();
                                                onAddToOutline({
                                                  title: chapter.title,
                                                  summary: analysis.summary,
                                                });
                                              }}
                                              disabled={addingToOutlineChapters.has(chapter.title)}
                                            >
                                              {addingToOutlineChapters.has(chapter.title)
                                                ? t('adding')
                                                : t('add_to_chapter_outline')}
                                            </Button>
                                          </div>
                                        )}
                                            </>
                                          );
                                        })()}
                                      </div>
                                      ) : (
                                        <div
                                          style={{
                                            padding: '1rem',
                                            backgroundColor: 'var(--card)',
                                            borderRadius: '6px',
                                            border: '1px solid var(--border)',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'flex-start',
                                            gap: '0.75rem',
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--muted-foreground)', lineHeight: '1.5' }}>
                                            {t('section_analysis_empty_prompt')}
                                          </p>
                                          {!chapter.isManual && (
                                            <AnalyzeButton
                                              onClick={(e) => {
                                                e?.stopPropagation();
                                                onAnalyzeChapter(chapter.title);
                                              }}
                                              isAnalyzing={analyzingChapters.has(chapter.title)}
                                              className="mr-0"
                                              tooltip={t('analyze_section_tooltip')}
                                            >
                                              {analyzingChapters.has(chapter.title)
                                                ? t('analyzing')
                                                : t('analyze')}
                                            </AnalyzeButton>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}
        </React.Fragment>
      ) : (
        <div style={isSidebar ? { textAlign: 'center', padding: '2rem', color: '#9ca3af' } : { textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
          <p style={{ fontSize: isSidebar ? '0.875rem' : '1.125rem' }}>{t('add_headings_for_chapters')}</p>
        </div>
      )}
    </>
  );
};

